import type { SchoolFilter } from '../../types/filters';
import { FilterId } from '../../types/filters';

type SchoolNameEntry = {
  rsid: string;
  name: string;
};

/**
 * Replaces the placeholder school labels (we initially only know an `rsid`) with the
 * resolved display names provided by the AI response payload.
 *
 * This is intentionally **best-effort**:
 * - if a school id isn't present in `schoolNames`, we keep showing the `rsid`
 */
export const resolveSchoolNamesInPlace = ({
  schoolInstancesByFilter,
  schoolNames,
}: {
  schoolInstancesByFilter: Map<FilterId, SchoolFilter[]>;
  schoolNames?: SchoolNameEntry[];
}): void => {
  const schoolFilters = schoolInstancesByFilter.get(FilterId.SCHOOL) || [];
  if (schoolFilters.length === 0) return;

  const schoolNameByRsid = new Map<string, string>(
    (schoolNames ?? []).map((entry) => [entry.rsid, entry.name])
  );

  const updated = schoolFilters.map((filter) => {
    const entries = Object.entries(filter.schoolResultItems || {});
    const resolvedEntries = entries.map(([key, school]) => {
      const rsid = school?.rsid ?? key;
      const displayName = schoolNameByRsid.get(rsid) ?? rsid;
      return [
        key,
        {
          label: displayName,
          name: displayName,
          primary_name: displayName,
          rsid,
        },
      ] as const;
    });

    return {
      ...filter,
      schoolResultItems: Object.fromEntries(resolvedEntries),
      isUserSubmitted: true,
    };
  });

  schoolInstancesByFilter.set(FilterId.SCHOOL, updated);
};
